import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  adminPreviewPartnerInvoice,
  adminCreatePartnerInvoice,
  adminSendPartnerInvoice,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";

const fmtEUR = (n) =>
  Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10);
const plus14 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

/**
 * PartnerInvoiceBuilderModal
 *
 * Geopend met een set BtwApplications die allemaal dezelfde partnerUserId
 * moeten hebben (validatie gebeurt in de parent voor de open-knop). Roept
 * de preview-endpoint aan, laat regels editable bewerken, slaat de factuur
 * op (draft) en optioneel direct verzenden.
 */
const PartnerInvoiceBuilderModal = ({
  isOpen,
  onClose,
  onCreated,
  partnerUserId,
  applicationIds,
  defaultBillingEmail,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [billingEmail, setBillingEmail] = useState(defaultBillingEmail ?? "");
  const [kenmerk, setKenmerk] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(plus14());
  const [notes, setNotes] = useState("");
  const [sendAfterCreate, setSendAfterCreate] = useState(true);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminPreviewPartnerInvoice({ partnerUserId, applicationIds })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        setPreview(data);
        setLines(data?.lines ?? []);
        if (!billingEmail) setBillingEmail(data?.partner?.billingEmail ?? "");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.error ??
            e?.message ??
            "Preview kon niet worden geladen.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, partnerUserId, applicationIds]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const totals = useMemo(() => {
    const subtotal = lines.reduce(
      (acc, l) => acc + Number(l.amountExcl ?? 0),
      0,
    );
    const vat = lines.reduce((acc, l) => acc + Number(l.vatAmount ?? 0), 0);
    const total = lines.reduce((acc, l) => acc + Number(l.amountIncl ?? 0), 0);
    return {
      subtotalExcl: Math.round(subtotal * 100) / 100,
      vatAmount: Math.round(vat * 100) / 100,
      totalIncl: Math.round(total * 100) / 100,
    };
  }, [lines]);

  const updateLineAmount = (idx, raw) => {
    const excl = Number(raw);
    if (!Number.isFinite(excl)) return;
    setLines((prev) => {
      const next = [...prev];
      const vatRate = next[idx].vatRate ?? 0.21;
      const vatAmount = Math.round(excl * vatRate * 100) / 100;
      next[idx] = {
        ...next[idx],
        amountExcl: excl,
        vatAmount,
        amountIncl: Math.round((excl + vatAmount) * 100) / 100,
      };
      return next;
    });
  };

  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateLineDescription = (idx, value) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], description: value };
      return next;
    });
  };

  const submit = async () => {
    if (lines.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        partnerUserId,
        lines,
        kenmerk: kenmerk || undefined,
        invoiceDate: invoiceDate || undefined,
        dueDate: dueDate || undefined,
        billingEmail: billingEmail || undefined,
        notes: notes || undefined,
      };
      const created = await adminCreatePartnerInvoice(payload);
      const invoice = created?.data ?? created;
      if (sendAfterCreate && invoice?._id) {
        await adminSendPartnerInvoice(invoice._id).catch((e) => {
          console.error("Versturen direct na aanmaken mislukt:", e);
          throw new Error(
            "Factuur is aangemaakt als draft, maar versturen mislukte. Probeer opnieuw vanaf het facturen-overzicht.",
          );
        });
      }
      onCreated?.(invoice, { sent: sendAfterCreate });
    } catch (e) {
      setError(
        e?.response?.data?.error ??
          e?.message ??
          "Factuur aanmaken is mislukt.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
          <div>
            <h2 className="font-display text-xl text-gray-900">
              Partner-factuur aanmaken
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {preview?.partner?.naam
                ? `Voor ${preview.partner.naam}`
                : "Selecteer aanvragen om te factureren."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Preview laden…
            </div>
          ) : error ? (
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
              {error}
            </div>
          ) : (
            <>
              {/* Header velden */}
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="font-medium text-gray-900">Kenmerk</span>
                  <input
                    type="text"
                    value={kenmerk}
                    onChange={(e) => setKenmerk(e.target.value)}
                    placeholder="(automatisch = factuurnummer)"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={submitting}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-900">
                    Facturatie-email
                  </span>
                  <input
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={submitting}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-900">Factuurdatum</span>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={submitting}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-900">Vervaldatum</span>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={submitting}
                  />
                </label>
              </div>

              {/* Regels */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Regels ({lines.length})
                </h3>
                {lines.length === 0 ? (
                  <p className="mt-3 rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                    Geen regels. Annuleer en selecteer aanvragen die nog niet
                    op een verstuurde factuur staan.
                  </p>
                ) : (
                  <div className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {lines.map((line, idx) => (
                      <div
                        key={String(line.btwApplicationId) + idx}
                        className="flex flex-wrap items-start gap-3 p-3"
                      >
                        <div className="flex-1 min-w-[200px]">
                          <p className="font-medium text-gray-900">
                            {line.customerName || "-"}
                          </p>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) =>
                              updateLineDescription(idx, e.target.value)
                            }
                            className="mt-1 w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700 focus:bg-white focus:outline-none"
                            disabled={submitting}
                          />
                          {line.alreadyInvoiced ? (
                            <p className="mt-1 text-xs text-amber-700">
                              Let op: stond al op een eerdere factuur.
                            </p>
                          ) : null}
                        </div>
                        <div className="w-32">
                          <label className="block text-xs text-gray-500">
                            Bedrag excl.
                          </label>
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-sm text-gray-400">&euro;</span>
                            <input
                              type="number"
                              step="0.01"
                              value={line.amountExcl}
                              onChange={(e) =>
                                updateLineAmount(idx, e.target.value)
                              }
                              className="w-full rounded-md border border-gray-200 px-2 py-1 text-right text-sm focus:border-emerald-500 focus:outline-none"
                              disabled={submitting}
                            />
                          </div>
                          <p className="mt-1 text-right text-xs text-gray-400">
                            incl. &euro; {fmtEUR(line.amountIncl)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          disabled={submitting}
                          className="mt-4 rounded p-1 text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label="Regel verwijderen"
                        >
                          <svg
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zm-3 6a1 1 0 112 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-72 rounded-lg bg-gray-50 p-3 text-sm">
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-500">Subtotaal (excl.)</span>
                    <span>&euro; {fmtEUR(totals.subtotalExcl)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-500">BTW (21%)</span>
                    <span>&euro; {fmtEUR(totals.vatAmount)}</span>
                  </div>
                  <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 font-semibold text-gray-900">
                    <span>Totaal (incl.)</span>
                    <span>&euro; {fmtEUR(totals.totalIncl)}</span>
                  </div>
                </div>
              </div>

              {/* Notes + send-flag */}
              <label className="mt-4 block text-sm">
                <span className="font-medium text-gray-900">
                  Opmerking (optioneel)
                </span>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  disabled={submitting}
                  placeholder="Wordt onder op de factuur en PDF getoond."
                />
              </label>

              <label className="mt-4 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sendAfterCreate}
                  onChange={(e) => setSendAfterCreate(e.target.checked)}
                  disabled={submitting}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span>Direct versturen naar partner-email na aanmaken</span>
              </label>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || loading || lines.length === 0 || !!error}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting
              ? "Bezig…"
              : sendAfterCreate
              ? "Aanmaken & versturen"
              : "Aanmaken als draft"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PartnerInvoiceBuilderModal;
